import type { FormBuilderField } from "../../types/formBuilder";
import type { PaginatedMeta } from "../../types/formList";
import type { FormSubmissionDto } from "../../types/submission";
import { getSubmissionFieldValue } from "./normalizeSubmission";
import { resolveFieldType } from "./normalizeFieldType";
import { buildClientPaginationMeta } from "./submissionPagination";

export const EMPTY_SUBMISSION_GROUP_LABEL = "Без значения";

const GROUPABLE_FIELD_TYPES = new Set([
  "city",
  "choice",
  "select",
  "multiple-choice",
  "privacy",
  "text",
  "date",
  "birthdate",
]);

const GROUPABLE_YANDEX_TYPES = new Set([
  "enum",
  "radio",
  "select",
  "checkbox",
  "choices",
  "boolean",
]);

const NUMERIC_YANDEX_TYPES = new Set([
  "number",
  "numeric",
  "integer",
  "decimal",
  "float",
]);

export type SubmissionsSortKey =
  | { kind: "id" }
  | { kind: "createdAt" }
  | { kind: "field"; fieldId: number };

export type SubmissionsSortDir = "asc" | "desc";

export type SubmissionsSortState = {
  key: SubmissionsSortKey;
  dir: SubmissionsSortDir;
};

export const DEFAULT_SUBMISSIONS_SORT: SubmissionsSortState = {
  key: { kind: "createdAt" },
  dir: "desc",
};

export const SUBMISSIONS_ID_COLUMN_KEY = "id";
export const SUBMISSIONS_DATE_COLUMN_KEY = "createdAt";
export const SUBMISSIONS_ROW_COLUMN_KEY = "row";

export function submissionsRowNumbers(
  pageItems: FormSubmissionDto[],
  currentPage: number,
  perPage: number,
): Map<number, number> {
  const start = (currentPage - 1) * perPage;
  return new Map(
    pageItems.map((item, index) => [item.id, start + index + 1] as const),
  );
}

export type SubmissionsTableRow =
  | { type: "group"; key: string; label: string; count: number }
  | { type: "submission"; submission: FormSubmissionDto };

export function sameSubmissionsSortKey(
  left: SubmissionsSortKey,
  right: SubmissionsSortKey,
): boolean {
  if (left.kind !== right.kind) return false;
  if (left.kind === "field" && right.kind === "field") {
    return left.fieldId === right.fieldId;
  }
  return true;
}

export function toggleSubmissionsSort(
  current: SubmissionsSortState,
  nextKey: SubmissionsSortKey,
): SubmissionsSortState {
  if (!sameSubmissionsSortKey(current.key, nextKey)) {
    return {
      key: nextKey,
      dir: nextKey.kind === "createdAt" ? "desc" : "asc",
    };
  }
  return { key: nextKey, dir: current.dir === "asc" ? "desc" : "asc" };
}

export function isGroupableSubmissionField(field: FormBuilderField): boolean {
  if (GROUPABLE_FIELD_TYPES.has(resolveFieldType(field.type))) return true;
  const yandexType = field.yandexType?.trim().toLowerCase();
  return Boolean(yandexType && GROUPABLE_YANDEX_TYPES.has(yandexType));
}

export function groupableSubmissionFields(
  fields: FormBuilderField[],
): FormBuilderField[] {
  return fields.filter(
    (field) => field.id != null && isGroupableSubmissionField(field),
  );
}

export function isNumericSubmissionField(field: FormBuilderField): boolean {
  if (resolveFieldType(field.type) === "number") return true;
  const yandexType = field.yandexType?.trim().toLowerCase();
  return Boolean(yandexType && NUMERIC_YANDEX_TYPES.has(yandexType));
}

function submissionTimestamp(submission: FormSubmissionDto): number {
  const raw = submission.createdAt ?? submission.created_at;
  if (!raw) return 0;
  const time = new Date(raw).getTime();
  return Number.isNaN(time) ? 0 : time;
}

const collator = new Intl.Collator("ru", {
  numeric: true,
  sensitivity: "base",
});

function isEmptyDisplayValue(value: string): boolean {
  const trimmed = value.trim();
  return trimmed === "" || trimmed === "—";
}

function compareDisplayValues(left: string, right: string): number {
  const leftEmpty = isEmptyDisplayValue(left);
  const rightEmpty = isEmptyDisplayValue(right);
  if (leftEmpty && rightEmpty) return 0;
  if (leftEmpty) return 1;
  if (rightEmpty) return -1;
  return collator.compare(left, right);
}

export function sortSubmissionsForTable(
  items: FormSubmissionDto[],
  sort: SubmissionsSortState,
  getFieldValue: (submission: FormSubmissionDto, fieldId: number) => string,
): FormSubmissionDto[] {
  const direction = sort.dir === "asc" ? 1 : -1;

  return [...items].sort((left, right) => {
    if (sort.key.kind === "id") {
      return (left.id - right.id) * direction;
    }

    if (sort.key.kind === "createdAt") {
      const byTime = submissionTimestamp(left) - submissionTimestamp(right);
      if (byTime !== 0) return byTime * direction;
      return (left.id - right.id) * direction;
    }

    const compared = compareDisplayValues(
      getFieldValue(left, sort.key.fieldId),
      getFieldValue(right, sort.key.fieldId),
    );
    if (compared !== 0) return sort.dir === "asc" ? compared : -compared;
    return left.id - right.id;
  });
}

function groupLabelForValue(value: string): string {
  return isEmptyDisplayValue(value)
    ? EMPTY_SUBMISSION_GROUP_LABEL
    : value.trim();
}

export function groupSubmissionsForTable(
  items: FormSubmissionDto[],
  fieldId: number,
  getFieldValue: (submission: FormSubmissionDto, fieldId: number) => string,
): Array<{ key: string; label: string; items: FormSubmissionDto[] }> {
  const buckets = new Map<string, FormSubmissionDto[]>();

  for (const item of items) {
    const label = groupLabelForValue(getFieldValue(item, fieldId));
    const bucket = buckets.get(label);
    if (bucket) bucket.push(item);
    else buckets.set(label, [item]);
  }

  return [...buckets.entries()]
    .sort(([left], [right]) => {
      if (left === EMPTY_SUBMISSION_GROUP_LABEL) return 1;
      if (right === EMPTY_SUBMISSION_GROUP_LABEL) return -1;
      return collator.compare(left, right);
    })
    .map(([label, grouped]) => ({ key: label, label, items: grouped }));
}

export type SubmissionsAnalyticsBucket = {
  label: string;
  count: number;
  percent: number;
};

export type SubmissionsAnalyticsField = {
  fieldId: number;
  label: string;
  buckets: SubmissionsAnalyticsBucket[];
  numericSummary?: {
    sum: number;
    valueCount: number;
    totalCount: number;
  };
};

export type SubmissionsNestedGroup = {
  label: string;
  count: number;
  percent: number;
  children: SubmissionsNestedGroup[];
};

function sortAnalyticsBuckets<T extends { label: string; count: number }>(
  buckets: T[],
): T[] {
  return [...buckets].sort((left, right) => {
    if (left.label === EMPTY_SUBMISSION_GROUP_LABEL) return 1;
    if (right.label === EMPTY_SUBMISSION_GROUP_LABEL) return -1;
    if (right.count !== left.count) return right.count - left.count;
    return collator.compare(left.label, right.label);
  });
}

function bucketsFromGroups(
  groups: Array<{ label: string; items: FormSubmissionDto[] }>,
  total: number,
): SubmissionsAnalyticsBucket[] {
  return sortAnalyticsBuckets(
    groups.map((group) => ({
      label: group.label,
      count: group.items.length,
      percent: total === 0 ? 0 : Math.round((group.items.length / total) * 100),
    })),
  );
}

function parseAnalyticsNumber(raw: unknown): number | null {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== "string") return null;

  const normalized = raw
    .trim()
    .replace(/[\s\u00a0]/g, "")
    .replace(",", ".");
  if (!/^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/.test(normalized)) return null;
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

function numericAnalyticsSummary(
  items: FormSubmissionDto[],
  field: FormBuilderField,
  fields: FormBuilderField[],
): { sum: number; valueCount: number; totalCount: number } {
  let sum = 0;
  let valueCount = 0;

  for (const item of items) {
    const value = parseAnalyticsNumber(
      getSubmissionFieldValue(item.data, field, fields),
    );
    if (value == null) continue;
    sum += value;
    valueCount += 1;
  }

  return { sum, valueCount, totalCount: items.length };
}

function nestedGroupsFromItems(
  items: FormSubmissionDto[],
  fieldIds: number[],
  parentTotal: number,
  getFieldValue: (submission: FormSubmissionDto, fieldId: number) => string,
): SubmissionsNestedGroup[] {
  const [fieldId, ...rest] = fieldIds;
  if (fieldId == null) return [];

  return sortAnalyticsBuckets(
    groupSubmissionsForTable(items, fieldId, getFieldValue).map((group) => ({
      label: group.label,
      count: group.items.length,
      percent:
        parentTotal === 0
          ? 0
          : Math.round((group.items.length / parentTotal) * 100),
      children:
        rest.length > 0
          ? nestedGroupsFromItems(
              group.items,
              rest,
              group.items.length,
              getFieldValue,
            )
          : [],
    })),
  );
}

export function buildSubmissionsAnalytics(
  items: FormSubmissionDto[],
  fields: FormBuilderField[],
  getFieldValue: (submission: FormSubmissionDto, fieldId: number) => string,
): SubmissionsAnalyticsField[] {
  const total = items.length;

  return fields.flatMap((field) => {
    const fieldId = field.id;
    if (fieldId == null) return [];

    if (isNumericSubmissionField(field)) {
      return [
        {
          fieldId,
          label: field.label.trim() || field.name || `Поле #${fieldId}`,
          buckets: [],
          numericSummary: numericAnalyticsSummary(items, field, fields),
        },
      ];
    }

    if (!isGroupableSubmissionField(field)) return [];

    return [
      {
        fieldId,
        label: field.label.trim() || field.name || `Поле #${fieldId}`,
        buckets: bucketsFromGroups(
          groupSubmissionsForTable(items, fieldId, getFieldValue),
          total,
        ),
      },
    ];
  });
}

export function buildSubmissionsNestedBreakdown(
  items: FormSubmissionDto[],
  fieldIds: number[],
  getFieldValue: (submission: FormSubmissionDto, fieldId: number) => string,
): SubmissionsNestedGroup[] {
  const uniqueIds = [...new Set(fieldIds.filter((id) => Number.isFinite(id)))];
  if (uniqueIds.length === 0) return [];
  return nestedGroupsFromItems(items, uniqueIds, items.length, getFieldValue);
}

export function buildSubmissionsTableView(
  items: FormSubmissionDto[],
  options: {
    sort: SubmissionsSortState;
    groupByFieldId: number | null;
    page: number;
    perPage: number;
    getFieldValue: (submission: FormSubmissionDto, fieldId: number) => string;
  },
): {
  sorted: FormSubmissionDto[];
  pageItems: FormSubmissionDto[];
  pageRows: SubmissionsTableRow[];
  meta: PaginatedMeta;
} {
  const sorted = sortSubmissionsForTable(
    items,
    options.sort,
    options.getFieldValue,
  );

  const grouped = options.groupByFieldId
    ? groupSubmissionsForTable(
        sorted,
        options.groupByFieldId,
        options.getFieldValue,
      )
    : [{ key: "", label: "", items: sorted }];

  const ordered = grouped.flatMap((group) => group.items);
  const counts = new Map(
    grouped.map((group) => [group.key, group.items.length] as const),
  );
  const meta = buildClientPaginationMeta(
    ordered.length,
    options.page,
    options.perPage,
  );
  const start = (meta.current_page - 1) * options.perPage;
  const pageItems = ordered.slice(start, start + options.perPage);

  const pageRows: SubmissionsTableRow[] = [];
  if (!options.groupByFieldId) {
    for (const submission of pageItems) {
      pageRows.push({ type: "submission", submission });
    }
    return { sorted, pageItems, pageRows, meta };
  }

  let lastKey: string | null = null;
  for (const submission of pageItems) {
    const label = groupLabelForValue(
      options.getFieldValue(submission, options.groupByFieldId),
    );
    if (label !== lastKey) {
      pageRows.push({
        type: "group",
        key: label,
        label,
        count: counts.get(label) ?? 0,
      });
      lastKey = label;
    }
    pageRows.push({ type: "submission", submission });
  }

  return { sorted, pageItems, pageRows, meta };
}
